import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '@acko-tech/building-blocks.ui.input';
import {
    PrimaryButton,
    SecondaryButton,
} from '@acko-tech/building-blocks.ui.button';
import SnackBar from 'library/snackbar';
import { ModalDesktop, ModalMobile } from '@acko-tech/building-blocks.ui.modal';

// utils
import { getDomainOrigin, getAxiosConfig } from 'utils/serverConfig';
import { isEmpty } from 'utils/generics';

// helpers
import { hasAllRequiredValues, IFieldProps, IFieldConfig } from './helpers';

// styles
import {
    Page,
    Card,
    FormBody,
    FieldWrapper,
    Footer,
    SubmitButtonWrapper,
    ModalText,
    ModalSubText,
    PageHeading,
} from './styles';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { MPosContext } from 'utils/context';
import sendSegmentEvent from 'utils/segment';
import {
    SegmentEventActionNames,
    SegmentEventNameTypes,
    SegmentEventActionTypes,
    SegmentEventPageTypes,
    SegmentEventButtonNames,
    SegmentEventPages,
} from 'utils/segment/constants';

interface IConfigurableFormProps {
    uiConfig: IFieldConfig[];
    initialValues?: Record<string, any>;
    formType: string;
    onSubmit: () => void;
    setErrorMessage: (message: string) => void;
    setShowErrorScreen: (show: boolean) => void;
    validator: (value: object) => void;
    shouldUpdateDetails: boolean;
}

function ConfigurableForm({
    uiConfig,
    initialValues = {},
    formType,
    onSubmit,
    validator,
    setErrorMessage,
    setShowErrorScreen,
    shouldUpdateDetails,
}: IConfigurableFormProps) {
    const { permissions, userDetails } = useContext(MPosContext);
    const [values, setValues] = useState<Record<string, any>>(initialValues);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [validationErr, setValidationErr] = useState({});
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
        useState(true);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const { isMobile } = useResponsiveDevice();
    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    useEffect(() => {
        const allRequiredValuesValid = hasAllRequiredValues(uiConfig, values);
        if (isEmpty(validationErr) && allRequiredValuesValid) {
            setIsContinueButtonDisabled(false);
        } else {
            setIsContinueButtonDisabled(true);
        }
    }, [values, validationErr, uiConfig]);

    useEffect(() => {
        if (showSuccessModal) {
            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPageTypes.DEALER_DETAILS_PAGE,
                    view_name: SegmentEventPages.VIEW_CONFIRMATION_MODAL,
                },
                { userDetails, isMobile }
            );
        }
    }, [showSuccessModal]);

    const handleFieldChange = (props: IFieldProps) => (e: any) => {
        const { key_name: key } = props;
        const value = e.target?.value;
        const error = validator({ value, ...props });
        setValidationErr((prev) => ({
            ...prev,
            [key]: error,
        }));
        setValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = () => {
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.SUBMIT_DETAILS,
                button_name: SegmentEventButtonNames.SUBMIT,
            },
            { userDetails, isMobile }
        );
        setShowSuccessModal(true);
    };

    const handleConfirmSubmit = async () => {
        const url = shouldUpdateDetails
            ? `${getDomainOrigin()}/api/mpos/genie/onboarding/${
                  permissions?.partner_id
              }/details/update?user_group=${permissions?.group}`
            : `${getDomainOrigin()}/api/mpos/genie/onboarding/${
                  permissions?.partner_id
              }/details/submit?user_group=${permissions?.group}`;
        try {
            const { status } = await axios({
                method: 'POST',
                url,
                ...getAxiosConfig(),
                data: {
                    form_type: formType,
                    form_data: values,
                },
            });
            if (status === 200) {
                setShowSuccessModal(false);
                onSubmit();
            }
        } catch (e: any) {
            if (e?.response?.status === 404) {
                setErrorMessage(e?.response?.data?.message);
            }
            setShowErrorScreen(true);
        }
    };

    const handleSnakbarComplete = () => {
        setSnackbar({
            type: '',
            message: '',
        });
    };

    const onModalCancel = () => {
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.CLICK_CANCEL_MODAL,
                button_name: SegmentEventButtonNames.CANCEL_MODAL,
            },
            { userDetails, isMobile }
        );
        setShowSuccessModal(false);
    };

    return (
        <Page>
            <Card
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <FormBody>
                    {uiConfig.map((field) => {
                        const key = field.props?.key_name || field.id;
                        if (field.component === 'input') {
                            return (
                                <FieldWrapper key={field.id}>
                                    <PageHeading>{field?.label}</PageHeading>
                                    <Input
                                        name={key}
                                        type={field.type}
                                        value={values[key] || ''}
                                        className={''}
                                        placeHolder={field.title}
                                        onChange={handleFieldChange(
                                            field.props
                                        )}
                                        errMsg={validationErr?.[key]}
                                        hasError={
                                            validationErr?.[key]?.length > 0
                                        }
                                        disabled={
                                            field.props?.editable === false
                                        }
                                    />
                                </FieldWrapper>
                            );
                        }

                        return null;
                    })}
                </FormBody>

                <Footer>
                    <SubmitButtonWrapper>
                        <PrimaryButton
                            type="button"
                            onClick={handleSubmit}
                            disabled={isContinueButtonDisabled}
                        >
                            Submit
                        </PrimaryButton>
                    </SubmitButtonWrapper>
                </Footer>
                <SnackBar
                    {...snackbar}
                    handleSnakbarComplete={handleSnakbarComplete}
                />
            </Card>
            <Modal
                header={<></>}
                urlhash={''}
                show={showSuccessModal}
                onHide={() => {
                    setShowSuccessModal(false);
                }}
                padding={'20px'}
                showOverlayBackground={true}
                maxWidth={'350px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                hideCloseButton={true}
            >
                <ModalText>
                    <strong>Are you sure?</strong>
                    <ModalSubText>
                        Incorrect information could lead to data inaccuracies.
                    </ModalSubText>
                </ModalText>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginTop: '16px',
                    }}
                >
                    <SecondaryButton type="button" onClick={onModalCancel}>
                        No
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={handleConfirmSubmit}>
                        Yes
                    </PrimaryButton>
                </div>
            </Modal>
        </Page>
    );
}

export default ConfigurableForm;
